"use strict";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Protocol } from "@uniswap/router-sdk";
import ms from "ms";
import {
  INTERNAL_ROUTER_PREFERENCE_PRICE,
  QuoteIntent,
  QuoteMethod,
  QuoteState,
  RouterPreference,
  URAQuoteType
} from "state/routing/types";
import { isExactInput, transformQuoteToTrade } from "state/routing/utils";
import { trace } from "tracing/trace";
import { InterfaceEventNameLocal } from "features/telemetry/constants";
import { sendAnalyticsEvent } from "features/telemetry/send";
import { logSwapQuoteFetch } from "features/transactions/swap/analytics";
import { logger } from "utilities/src/logger/logger";
const UNISWAP_GATEWAY_DNS_URL = process.env.REACT_APP_UNISWAP_GATEWAY_DNS;
if (UNISWAP_GATEWAY_DNS_URL === void 0) {
  throw new Error(`UNISWAP_GATEWAY_DNS_URL must be defined environment variables`);
}
const protocols = [Protocol.V2, Protocol.V3, Protocol.MIXED];
const DEFAULT_QUERY_PARAMS = {
  // this should be removed once BE fixes issue where enableUniversalRouter is required for fees to work
  enableUniversalRouter: true
};
function getRoutingAPIConfig(args) {
  const { account, uniswapXForceSyntheticQuotes, routerPreference, protocolPreferences, routingType } = args;
  const uniswapX = {
    useSyntheticQuotes: uniswapXForceSyntheticQuotes,
    swapper: account,
    routingType: URAQuoteType.DUTCH_V1
  };
  const uniswapXPriorityOrders = {
    routingType: URAQuoteType.PRIORITY,
    swapper: account
  };
  const uniswapXv2 = {
    useSyntheticQuotes: uniswapXForceSyntheticQuotes,
    swapper: account,
    routingType: URAQuoteType.DUTCH_V2
  };
  const uniswapXv3 = {
    useSyntheticQuotes: uniswapXForceSyntheticQuotes,
    swapper: account,
    routingType: URAQuoteType.DUTCH_V3
  };
  const classic = {
    ...DEFAULT_QUERY_PARAMS,
    protocols: protocolPreferences && protocolPreferences.length > 0 ? protocolPreferences : protocols,
    routingType: URAQuoteType.CLASSIC,
    recipient: account,
    enableFeeOnTransferFeeFetching: true
  };
  if (
    // If the user has opted out of UniswapX during the opt-out transition period, we should respect that preference and only request classic quotes.
    routerPreference === RouterPreference.API || routerPreference === INTERNAL_ROUTER_PREFERENCE_PRICE || routingType === URAQuoteType.CLASSIC
  ) {
    return [classic];
  }
  let uniswapXConfig;
  switch (routingType) {
    case URAQuoteType.PRIORITY:
      uniswapXConfig = uniswapXPriorityOrders;
      break;
    case URAQuoteType.DUTCH_V3:
      uniswapXConfig = uniswapXv3;
      break;
    case URAQuoteType.DUTCH_V2:
      uniswapXConfig = uniswapXv2;
      break;
    default:
      uniswapXConfig = uniswapX;
  }
  return [uniswapXConfig, classic];
}
export const routingApi = createApi({
  reducerPath: "routingApi",
  baseQuery: fetchBaseQuery(),
  endpoints: (build) => ({
    getQuote: build.query({
      queryFn(args, _api, _extraOptions, fetch) {
        return trace({ name: "Quote", op: "quote", data: { ...args } }, async (trace2) => {
          logSwapQuoteFetch({
            chainId: args.tokenInChainId,
            isUSDQuote: args.routerPreference === INTERNAL_ROUTER_PREFERENCE_PRICE
          });
          const {
            tokenInAddress: tokenIn,
            tokenInChainId,
            tokenOutAddress: tokenOut,
            tokenOutChainId,
            amount,
            tradeType,
            sendPortionEnabled
          } = args;
          const requestBody = {
            tokenInChainId,
            tokenIn,
            tokenOutChainId,
            tokenOut,
            amount,
            sendPortionEnabled,
            type: isExactInput(tradeType) ? "EXACT_INPUT" : "EXACT_OUTPUT",
            intent: args.routerPreference === INTERNAL_ROUTER_PREFERENCE_PRICE ? QuoteIntent.Pricing : QuoteIntent.Quote,
            configs: getRoutingAPIConfig(args),
            useUniswapX: args.routerPreference === RouterPreference.X,
            swapper: args.account
          };
          try {
            return trace2.child({ name: "Quote on server", op: "quote.server" }, async () => {
              const response = await fetch({
                method: "POST",
                url: `${UNISWAP_GATEWAY_DNS_URL}/quote`,
                body: JSON.stringify(requestBody),
                headers: {
                  "x-request-source": "uniswap-web"
                }
              });
              if (response.error) {
                try {
                  const errorData = response.error.data;
                  if (typeof errorData === "object" && (errorData?.errorCode === "NO_ROUTE" || errorData?.detail === "No quotes available")) {
                    sendAnalyticsEvent(InterfaceEventNameLocal.NoQuoteReceivedFromRoutingAPI, {
                      requestBody,
                      response,
                      routerPreference: args.routerPreference
                    });
                    return {
                      data: { state: QuoteState.NOT_FOUND, latencyMs: trace2.now() }
                    };
                  }
                } catch {
                  throw response.error;
                }
              }
              const uraQuoteResponse = response.data;
              const tradeResult = await transformQuoteToTrade(args, uraQuoteResponse, QuoteMethod.ROUTING_API);
              return { data: { ...tradeResult, latencyMs: trace2.now() } };
            });
          } catch (error) {
            logger.warn(
              "routing/slice",
              "queryFn",
              `GetQuote failed on Unified Routing API, falling back to client: ${error?.message ?? error?.detail ?? error}`
            );
          }
          return {
            data: { state: QuoteState.NOT_FOUND, latencyMs: trace2.now() }
          };
        });
      },
      keepUnusedDataFor: ms(`10s`),
      extraOptions: {
        maxRetries: 0
      }
    })
  })
});
export const { useGetQuoteQuery } = routingApi;
export const useGetQuoteQueryState = routingApi.endpoints.getQuote.useQueryState;
